import PropTypes from 'prop-types';

import { QUESTION_GROUP_LOGIC, QUESTION_GROUP_LOGIC_BRANCHES } from '@api/instant_survey';
import { Card, Layout } from '@utilities';

import { firstGroupSubtreeQuestionIndex } from '../../../../../../utilities';

import {
  answerOperators,
  disqualifyOperators,
} from '../../../../../QuestionLogic/utilities/constants';
import DisqualifyLogicRow from '../../../../../QuestionLogic/components/DisqualifyLogicRow';
import GroupLogicRow from '../../../QuestionGroupBlock/components/ModalQuestionGroupLogic/components/QuestionGroupLogic/components/GroupLogicRow';
import OptionLogicRow from '../../../Question/components/ModalAnswerOptionLogic/components/OptionLogic/components/OptionLogicRow';
import QuestionLogicOptions from '../../../../../QuestionLogic/components/QuestionLogicOptions';
import SkipLogicRow from '../../../../../QuestionLogic/components/SkipLogicRow';

const QuestionLogicCondition = ({
  answerId,
  branch = 'skip',
  groupId,
  question,
  questionGroups = {},
  questionLogic,
  selectedQuestions,
  type,
}) => {
  const isGroup = type === 'group';
  const disqualifyLogic = questionLogic[question?.id]?.disqualify;
  const logicList = isGroup
    ? questionLogic?.[QUESTION_GROUP_LOGIC]?.[groupId]?.[branch]?.logic || []
    : type === 'option'
      ? questionLogic?.[question.id]?.option?.[answerId]?.logic || []
      : questionLogic?.[question.id]?.skip?.logic || [];

  // A row is now invalid when its source question sits after that position
  const referenceIndex = isGroup
    ? firstGroupSubtreeQuestionIndex({
        groupId,
        questionGroups,
        questions: selectedQuestions || [],
      })
    : (selectedQuestions || []).findIndex(({ id }) => id === question?.id);

  const logicIndexes = logicList.reduce((acc, logicItem, index) => {
    if (!selectedQuestions || referenceIndex === -1) return acc;

    const logicQuestionId = logicItem?.question?.id;
    const logicQuestionIndex = selectedQuestions.findIndex(({ id }) => id === logicQuestionId);
    if (logicQuestionIndex === -1) return acc;

    const isInvalid = isGroup
      ? logicQuestionIndex >= referenceIndex
      : logicQuestionIndex > referenceIndex;
    if (isInvalid) acc.push(index);
    return acc;
  }, []);

  return (
    <Card>
      <h4>Condition</h4>
      <Layout.Flex.Column gap="small">
        {type === 'disqualify' && (
          <div>
            {Array.isArray(disqualifyLogic?.logic) ? (
              <>
                <span>Disqualify if&nbsp;</span>
                <QuestionLogicOptions
                  isCompleted
                  options={disqualifyOperators}
                  optionsLabel="Operator"
                  optionsType="radio"
                  value={disqualifyLogic.operator}
                />
                <span>&nbsp;apply</span>
                {(disqualifyLogic.logic || []).map((_, index) => (
                  <DisqualifyLogicRow
                    index={index}
                    isCompleted
                    key={index}
                    question={question}
                    questionId={question.id}
                    questionLogic={questionLogic}
                    setQuestionLogic={() => {}}
                  />
                ))}
              </>
            ) : (
              <>
                <span>Disqualify if the answer is&nbsp;</span>
                <QuestionLogicOptions
                  isCompleted
                  options={answerOperators}
                  optionsLabel="Operator"
                  optionsType="radio"
                  value={disqualifyLogic.operator}
                />
                <span>:&nbsp;</span>
                <QuestionLogicOptions
                  isCompleted
                  options={question.answers}
                  optionsLabel="Answer Options"
                  optionsType="checkbox"
                  value={disqualifyLogic.answers}
                />
              </>
            )}
          </div>
        )}
        {type === 'option' &&
          logicIndexes.map((index) => (
            <OptionLogicRow
              answerId={Number(answerId)}
              index={index}
              isCompleted
              key={index}
              questionId={question.id}
              questionIndex={selectedQuestions?.findIndex(({ id }) => id === question.id)}
              questionList={selectedQuestions}
              questionLogic={questionLogic}
              setQuestionLogic={() => {}}
            />
          ))}
        {type === 'skip' &&
          logicIndexes.map((index) => (
            <SkipLogicRow
              index={index}
              isCompleted
              key={index}
              questionId={question.id}
              questionList={selectedQuestions}
              questionLogic={questionLogic}
            />
          ))}
        {isGroup &&
          logicIndexes.map((index) => (
            <GroupLogicRow
              branch={branch}
              groupId={groupId}
              index={index}
              isCompleted
              key={index}
              questionIndex={referenceIndex}
              questionList={selectedQuestions}
              questionLogic={questionLogic}
              setQuestionLogic={() => {}}
            />
          ))}
      </Layout.Flex.Column>
    </Card>
  );
};

QuestionLogicCondition.propTypes = {
  answerId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  // Which branch of a group's logic to render; only meaningful when type is 'group'.
  branch: PropTypes.oneOf(QUESTION_GROUP_LOGIC_BRANCHES),
  groupId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  question: PropTypes.object,
  // Needed to locate a group by its subtree; only meaningful when type is 'group'.
  questionGroups: PropTypes.object,
  questionLogic: PropTypes.object.isRequired,
  selectedQuestions: PropTypes.array,
  type: PropTypes.oneOf(['disqualify', 'group', 'option', 'skip']).isRequired,
};

export default QuestionLogicCondition;
